import * as Sentry from '@sentry/browser';
import { appClient } from 'src/config/apollo';
import {
    PossibleReportAndSectionType as ReportOrSection,
    ReportTypeEnum,
} from 'src/generated/webapp_gql';
import {
    GetAdminInfoDocument,
    GetAdminInfoQuery,
} from 'src/modules/admin/documents/getAdminInfo.generated';
import { GetAllReportsAndSectionsDocument } from 'src/modules/admin/documents/getAllReportsAndSections.generated';
import {
    GetLookerSsoLinkDocument,
    GetLookerSsoLinkQuery,
} from 'src/modules/admin/documents/getLookerSSOLink.generated';

interface FetchLookerSSOResponse {
    url?: string;
    error?: string;
}

export class AdminService {
    public async fetchAdminInfo(): Promise<GetAdminInfoQuery | null> {
        try {
            const { data } = await appClient.query({
                query: GetAdminInfoDocument,
                fetchPolicy: 'network-only',
            });

            return data;
        } catch (error) {
            Sentry.captureException(error);
        }
        return null;
    }
    public async fetchLookerSSO(
        reportEnum: string = ReportTypeEnum.Darjeeling,
    ): Promise<FetchLookerSSOResponse | null> {
        try {
            const { data } = await appClient.query<GetLookerSsoLinkQuery>({
                query: GetLookerSsoLinkDocument,
                variables: { reportType: reportEnum },
                fetchPolicy: 'no-cache',
            });

            if (data.lookerSsoEmbed && 'debug' in data.lookerSsoEmbed) {
                if (data.lookerSsoEmbed.debug) {
                    Sentry.captureMessage(data.lookerSsoEmbed.debug, Sentry.Severity.Error);
                }

                return { error: data.lookerSsoEmbed.message ?? '' };
            }

            if (data?.lookerSsoEmbed?.__typename === 'LookerSsoEmbedLink') {
                return { url: data.lookerSsoEmbed.url ?? '' };
            }
        } catch (error) {
            Sentry.captureException(error);
        }

        return null;
    }
    public async fetchReportsInfo(): Promise<ReportOrSection[] | null> {
        try {
            const { data } = await appClient.query({
                query: GetAllReportsAndSectionsDocument,
                fetchPolicy: 'no-cache',
            });
            const { getAllReportsAndSections: reportsAndSections } = data;

            // Check if the response is an error and if the error field is not empty
            if (reportsAndSections && 'debug' in reportsAndSections) {
                if (reportsAndSections.debug) {
                    Sentry.captureMessage(data.lookerSsoEmbed.debug, Sentry.Severity.Error);
                }

                throw reportsAndSections.message ?? '';
            }

            return reportsAndSections.reportsOrSections || [];
        } catch (error) {
            Sentry.captureException(error);
        }
        return null;
    }
}
